import { emptySplitApi } from '../../../store/emptyApi';
import type {
  Team,
  TeamDetail,
  TeamsResponse,
  EmployeeTeam,
  EmployeeTeamsResponse,
  OrganizationMembersResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  EmployeeTeamsFilter,
  AddTeamMemberRequest,
  UpdateTeamMemberRequest,
  TeamLeadersResponse,
  SetLeaderRequest,
  SetLeaderResponse,
  EndLeadershipRequest,
  EndLeadershipResponse,
  SetMembersRequest,
  SetMembersResponse,
} from '../types';

export const teamApi = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    // Teams - My teams (teams user leads, is member of, or has direct reports on)
    getMyTeams: build.query<TeamsResponse, void>({
      query: () => ({
        url: '/api/portal/v1/teams/mine/',
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Team' as const, id })),
              { type: 'Team', id: 'MY_LIST' },
            ]
          : [{ type: 'Team', id: 'MY_LIST' }],
    }),

    // Teams - All teams in organization (admin view)
    getAllTeams: build.query<TeamsResponse, { status?: 'active' | 'archived' | 'all' } | void>({
      query: (args) => ({
        url: '/api/portal/v1/teams/all/',
        method: 'GET',
        params: args?.status ? { status: args.status } : undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Team' as const, id })),
              { type: 'Team', id: 'ALL_LIST' },
            ]
          : [{ type: 'Team', id: 'ALL_LIST' }],
    }),

    getLeaderTeams: build.query<TeamsResponse, void>({
      query: () => ({
        url: '/api/portal/v1/teams/leader-teams/',
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Team' as const, id })),
              { type: 'Team', id: 'LEADER_LIST' },
            ]
          : [{ type: 'Team', id: 'LEADER_LIST' }],
    }),

    getTeamDetail: build.query<TeamDetail, number>({
      query: (id) => ({
        url: `/api/portal/v1/teams/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Team', id }],
    }),

    getOrganizationMembers: build.query<OrganizationMembersResponse, void>({
      query: () => ({
        url: '/api/portal/v1/teams/organization-members/',
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Team' as const, id: `org-member-${id}` })),
              { type: 'Team', id: 'ORGANIZATION_MEMBERS' },
            ]
          : [{ type: 'Team', id: 'ORGANIZATION_MEMBERS' }],
    }),

    createTeam: build.mutation<Team, CreateTeamRequest>({
      query: (body) => ({
        url: '/api/portal/v1/teams/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Team', id: 'MY_LIST' }, { type: 'Team', id: 'ALL_LIST' }],
    }),

    updateTeam: build.mutation<Team, UpdateTeamRequest>({
      query: ({ id, ...body }) => ({
        url: `/api/portal/v1/teams/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Team', id },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'ALL_LIST' },
      ],
    }),

    deleteTeam: build.mutation<void, number>({
      query: (id) => ({
        url: `/api/portal/v1/teams/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Team', id },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'ALL_LIST' },
      ],
    }),

    archiveTeam: build.mutation<{ detail: string; is_archived: boolean; archived_on: string | null }, number>({
      query: (id) => ({
        url: `/api/portal/v1/teams/${id}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Team', id },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'ALL_LIST' },
      ],
    }),

    unarchiveTeam: build.mutation<{ detail: string; is_archived: boolean; archived_on: string | null }, number>({
      query: (id) => ({
        url: `/api/portal/v1/teams/${id}/unarchive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Team', id },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'ALL_LIST' },
      ],
    }),

    // Employee Teams
    getEmployeeTeams: build.query<EmployeeTeamsResponse, EmployeeTeamsFilter | void>({
      query: (params) => ({
        url: '/api/portal/v1/employee-teams/',
        method: 'GET',
        params: params ? {
          ...(params.employee_id && { employee_id: params.employee_id }),
          ...(params.team_id && { team_id: params.team_id }),
          ...(params.active_only !== undefined && { active_only: params.active_only }),
          ...(params.limit !== undefined && { limit: params.limit }),
        } : {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Team' as const, id: `member-${id}` })),
              { type: 'Team', id: 'MEMBERS_LIST' },
            ]
          : [{ type: 'Team', id: 'MEMBERS_LIST' }],
    }),

    addTeamMember: build.mutation<EmployeeTeam, AddTeamMemberRequest>({
      query: (body) => ({
        url: '/api/portal/v1/employee-teams/',
        method: 'POST',
        body: {
          employee: body.employee,
          team: body.team,
          start_date: body.start_date || new Date().toISOString(),
          end_date: body.end_date || null,
        },
      }),
      invalidatesTags: (result, error, { team }) => [
        { type: 'Team', id: team },
        { type: 'Team', id: 'MEMBERS_LIST' },
      ],
    }),

    updateTeamMember: build.mutation<EmployeeTeam, UpdateTeamMemberRequest>({
      query: ({ id, ...body }) => ({
        url: `/api/portal/v1/employee-teams/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Team' as const, id: `member-${id}` },
        { type: 'Team' as const, id: 'MEMBERS_LIST' },
        ...(result ? [{ type: 'Team' as const, id: result.team_id }] : []),
      ],
    }),

    removeTeamMember: build.mutation<void, number>({
      query: (id) => ({
        url: `/api/portal/v1/employee-teams/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Team', id: `member-${id}` },
        { type: 'Team', id: 'MEMBERS_LIST' },
      ],
    }),

    validateTeamImport: build.mutation<any, FormData>({
      query: (body) => ({
        url: '/api/portal/v1/teams/import/?mode=validate',
        method: 'POST',
        body,
      }),
    }),

    importTeams: build.mutation<any, FormData>({
      query: (body) => ({
        url: '/api/portal/v1/teams/import/?mode=import',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Team', id: 'ALL_LIST' }, { type: 'Team', id: 'MY_LIST' }],
    }),

    rebuildTeamMembers: build.mutation<any, void>({
      query: () => ({
        url: '/api/portal/v1/teams/rebuild-members/',
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'Team', id: 'ALL_LIST' },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'MEMBERS_LIST' },
      ],
    }),

    generateTeamExternalIds: build.mutation<any, void>({
      query: () => ({
        url: '/api/portal/v1/teams/generate-external-ids/',
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'Team', id: 'ALL_LIST' },
        { type: 'Team', id: 'MY_LIST' },
      ],
    }),

    getTeamImportTemplate: build.query<any, void>({
      query: () => ({
        url: '/api/portal/v1/teams/import-template/',
        method: 'GET',
      }),
    }),

    // Team Leadership
    getTeamLeaders: build.query<TeamLeadersResponse, number>({
      query: (teamId) => ({
        url: `/api/portal/v1/teams/${teamId}/leaders/`,
        method: 'GET',
      }),
      providesTags: (result, error, teamId) => [
        { type: 'Team', id: `leaders-${teamId}` },
      ],
    }),

    setTeamLeader: build.mutation<SetLeaderResponse, SetLeaderRequest>({
      query: ({ teamId, ...body }) => ({
        url: `/api/portal/v1/teams/${teamId}/set-leader/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: 'Team', id: teamId },
        { type: 'Team', id: `leaders-${teamId}` },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'ALL_LIST' },
      ],
    }),

    endTeamLeadership: build.mutation<EndLeadershipResponse, EndLeadershipRequest>({
      query: ({ teamId, ...body }) => ({
        url: `/api/portal/v1/teams/${teamId}/end-leadership/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: 'Team', id: teamId },
        { type: 'Team', id: `leaders-${teamId}` },
        { type: 'Team', id: 'MY_LIST' },
        { type: 'Team', id: 'ALL_LIST' },
      ],
    }),


  }),
  overrideExisting: false,
});

export const {
  useGetMyTeamsQuery,
  useGetLeaderTeamsQuery,
  useGetAllTeamsQuery,
  useGetTeamDetailQuery,
  useGetOrganizationMembersQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useArchiveTeamMutation,
  useUnarchiveTeamMutation,
  useGetEmployeeTeamsQuery,
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetTeamLeadersQuery,
  useSetTeamLeaderMutation,
  useEndTeamLeadershipMutation,
  useValidateTeamImportMutation,
  useImportTeamsMutation,
  useRebuildTeamMembersMutation,
  useGenerateTeamExternalIdsMutation,
  useGetTeamImportTemplateQuery,
} = teamApi;
